import { todayLocal } from '@/lib/dates'
import { useState } from 'react'
import { AlertTriangle, Plus } from 'lucide-react'
import { Link } from 'react-router'
import {
  CurrencyValue, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge, EmployeePicker } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useExpenseClaims, useExpenseMutations } from '../api'
import { CLAIM_STATUS_LABELS } from '../types'
import type { ClaimStatus } from '../types'
import { DatePicker } from '@/components/ui/date-picker'

const STATUS_TONE: Record<ClaimStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  APPROVED: 'info',
  PARTIALLY_APPROVED: 'warning',
  REJECTED: 'danger',
  REIMBURSED: 'success',
  CANCELLED: 'neutral',
}

/** Every claim, what it came to, and how much of it was allowed. */
export function ExpenseClaimsPage() {
  useBreadcrumb()
  const claims = useExpenseClaims()
  const { createClaim } = useExpenseMutations()

  const [draft, setDraft] = useState<{
    employeeId: string; title: string; claimDate: string; currency: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Expense claims"
        subtitle="What people spent, what was allowed, and whether they have been paid back."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                employeeId: '', title: '',
                claimDate: todayLocal(), currency: 'AED',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New claim
          </Button>
        }
      />

      <Card>
        {claims.isPending && <Skeleton className="m-4 h-24" />}
        {claims.isError && <ErrorState error={claims.error} onRetry={() => void claims.refetch()} />}
        {claims.isSuccess && claims.data.length === 0 && (
          <EmptyState
            title="Nothing has been claimed"
            description="A claim is raised as a draft, has its lines and receipts added, and is then submitted for approval."
          />
        )}
        {claims.isSuccess && claims.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Reference</th>
                  <th className="p-3 text-start font-medium">Person</th>
                  <th className="p-3 text-start font-medium">What for</th>
                  <th className="p-3 text-start font-medium">Date</th>
                  <th className="p-3 text-end font-medium">Claimed</th>
                  <th className="p-3 text-end font-medium">Approved</th>
                  <th className="p-3 text-start font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {claims.data.map((claim) => (
                  <tr key={claim.id} className="border-b border-slate-100">
                    <td className="p-3">
                      <Link
                        className="font-mono underline underline-offset-2"
                        to={`/expenses/claims/${claim.id}`}
                      >
                        {claim.reference}
                      </Link>
                    </td>
                    <td className="p-3">{claim.employeeName ?? claim.employeeCode}</td>
                    <td className="p-3 text-slate-600">
                      {claim.title}
                      {claim.overLimitLineCount > 0 && (
                        <span className="ms-2 inline-flex items-center gap-1 text-amber-700">
                          <AlertTriangle className="size-3.5" aria-hidden="true" />
                          {claim.overLimitLineCount} over policy
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-slate-600">{claim.claimDate}</td>
                    <td className="p-3 text-end">
                      <CurrencyValue value={claim.totalClaimed} currency={claim.currency} />
                    </td>
                    <td className="p-3 text-end font-medium">
                      <CurrencyValue value={claim.totalApproved} currency={claim.currency} />
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={claim.status}
                        tone={STATUS_TONE[claim.status as ClaimStatus] ?? 'neutral'}
                        label={CLAIM_STATUS_LABELS[claim.status as ClaimStatus] ?? claim.status}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        title="New expense claim"
        description="The claim is created empty. Lines and their receipts are added on the claim itself."
        submitLabel="Create claim"
        loading={createClaim.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await createClaim.mutateAsync({
                employeeId: draft.employeeId,
                title: draft.title,
                claimDate: draft.claimDate,
                currency: draft.currency,
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That claim could not be created')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="claim-employee">Person</Label>
              <EmployeePicker
                  id="claim-employee"
                  value={draft.employeeId}
                  onChange={(employeeId) => setDraft({ ...draft, employeeId: employeeId ?? '' })}
                />
            </div>
            <div>
              <Label htmlFor="claim-title">What for</Label>
              <Input
                id="claim-title" value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="claim-date">Claim date</Label>
              <DatePicker
                id="claim-date" value={draft.claimDate}
                onChange={(event) => setDraft({ ...draft, claimDate: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="claim-currency">Currency</Label>
              <Input
                id="claim-currency" maxLength={3} value={draft.currency}
                onChange={(event) =>
                  setDraft({ ...draft, currency: event.target.value.toUpperCase() })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
